/**
 * Executa `task` para cada item com no máximo `limit` tarefas em voo.
 *
 * Existe para as varreduras de vidas do PME (excluir duplicadas, sincronizar
 * lotes): laços `for … await` faziam uma requisição por vez — com centenas de
 * vidas, minutos de espera. `Promise.all` cru resolveria a lentidão mas dispara
 * tudo de uma vez e o gateway/InsureMO passa a devolver 429/timeout.
 *
 * Nunca rejeita: devolve o resultado de cada item no formato
 * `PromiseSettledResult`, preservando a ordem da entrada.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>
): Promise<PromiseSettledResult<R>[]> {
  const results = new Array<PromiseSettledResult<R>>(items.length);
  if (items.length === 0) return results;

  const size = Math.max(1, Math.min(limit, items.length));
  let cursor = 0;

  const worker = async (): Promise<void> => {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) return;
      try {
        results[index] = {
          status: 'fulfilled',
          value: await task(items[index], index),
        };
      } catch (reason) {
        results[index] = { status: 'rejected', reason };
      }
    }
  };

  await Promise.all(Array.from({ length: size }, worker));
  return results;
}
